(ns quuppa.qsm.planner.polygon-editor
  (:require [clojure.string :as str]
            [quuppa.qsm.planner.utils :as utils]
            [re-frame.core :as rf]))

(defn polygon-editing-overlay
  "SVG overlay for drawing and editing polygons (routes or geofences).
   Form-2 component to isolate atom re-renders from parent.
   - Click and drag on corners to move individual points
   - Click and drag on path lines to move the entire polygon
   - Alt+click on path lines to add new corner points

   Parameters:
   - polygon-type: 'route' or 'geofence'
   - img-width, img-height: dimensions for viewport
   - plan-id, building-id, level-id, polygon-id: identifiers
   - polygon-color: stroke and fill color
   - stored-db-points: points from database
   - current-points-atom: reagent atom to track current editing state
   - update-event: re-frame event keyword for updates (e.g., ::e/update-route-points)
   - fill-opacity: opacity for fill (0 for no fill, 0.2 for semi-transparent)"
  [_polygon-type _img-width _img-height _plan-id _building-id _level-id _polygon-id _polygon-color _stored-db-points _current-points-atom _update-event _fill-opacity]
  ;; Form-2: setup phase - return a render function
  (fn [polygon-type img-width img-height plan-id building-id level-id polygon-id polygon-color stored-db-points current-points-atom update-event fill-opacity]
    ;; Initialize atom from stored points if needed
    (when (and (seq stored-db-points) (empty? @current-points-atom))
      (reset! current-points-atom stored-db-points))
    (let [color (or polygon-color "#0d6efd")
          ;; Default rectangle - small centered rectangle (about 1/4 of image)
          default-points (let [w (or img-width 800)
                               h (or img-height 600)
                               rect-w (/ w 4)
                               rect-h (/ h 4)
                               start-x (- (/ w 2) (/ rect-w 2))
                               start-y (- (/ h 2) (/ rect-h 2))]
                           [[start-x start-y]
                            [(+ start-x rect-w) start-y]
                            [(+ start-x rect-w) (+ start-y rect-h)]
                            [start-x (+ start-y rect-h)]])
          ;; Use atom points if available, otherwise use default
          stored-points @current-points-atom
          points (if (empty? stored-points) default-points stored-points)

          ;; Function to save current points to db
          save-points-to-db (fn []
                              (when (and plan-id building-id level-id polygon-id (seq @current-points-atom))
                                (rf/dispatch [update-event plan-id building-id level-id polygon-id @current-points-atom])))

          ;; Handler to drag the entire path
          on-path-mouse-down
          (fn [e]
            (.stopPropagation e)
            (.preventDefault e)
            ;; Initialize atom with current points if empty (only on first interaction)
            (when (empty? @current-points-atom)
              (reset! current-points-atom points))
            (let [svg-el (.-currentTarget e)
                  rect (.getBoundingClientRect (.-ownerSVGElement svg-el))
                  click-x (- (.-clientX e) (.-left rect))
                  click-y (- (.-clientY e) (.-top rect))
                  alt-pressed? (.-altKey e)]
              (if alt-pressed?
                ;; Alt+click: add a new point on the closest segment
                (let [{:keys [segment-idx t]} (utils/find-closest-segment @current-points-atom click-x click-y)
                      pts @current-points-atom
                      n (count pts)
                      ;; Handle closing segment (last to first) specially
                      [p1 p2] (if (= segment-idx (dec n))
                                [(nth pts segment-idx) (nth pts 0)]
                                [(nth pts segment-idx) (nth pts (inc segment-idx))])
                      new-x (+ (first p1) (* t (- (first p2) (first p1))))
                      new-y (+ (second p1) (* t (- (second p2) (second p1))))]
                  (swap! current-points-atom
                         (fn [pts]
                           (if (= segment-idx (dec (count pts)))
                             ;; Insert at end for closing segment
                             (conj pts [new-x new-y])
                             ;; Insert after segment-idx for other segments
                             (vec (concat (take (inc segment-idx) pts)
                                          [[new-x new-y]]
                                          (drop (inc segment-idx) pts))))))
                  ;; Save after adding point
                  (save-points-to-db))
                ;; Normal click: drag the entire path
                (let [start-x (.-clientX e)
                      start-y (.-clientY e)
                      initial-points @current-points-atom
                      on-move (fn [me]
                                (let [dx (- (.-clientX me) start-x)
                                      dy (- (.-clientY me) start-y)]
                                  (reset! current-points-atom
                                          (mapv (fn [[x y]] [(+ x dx) (+ y dy)])
                                                initial-points))))
                      on-up (fn on-up-fn [_]
                              (.removeEventListener js/document "mousemove" on-move)
                              (.removeEventListener js/document "mouseup" on-up-fn)
                              ;; Save after drag completes
                              (save-points-to-db))]
                  (.addEventListener js/document "mousemove" on-move)
                  (.addEventListener js/document "mouseup" on-up)))))

          ;; Handler to drag individual corner points or remove with alt+right click
          on-corner-mouse-down
          (fn [idx e]
            (.stopPropagation e)
            (.preventDefault e)
            ;; Initialize atom with current points if empty (only on first interaction)
            (when (empty? @current-points-atom)
              (reset! current-points-atom points))
            (let [alt-pressed? (.-altKey e)
                  right-click? (= (.-button e) 2)]
              (if (and alt-pressed? right-click?)
                ;; Alt+right click: remove the point (if more than 3 points)
                (do
                  (when (> (count @current-points-atom) 3)
                    (swap! current-points-atom
                           (fn [pts]
                             (vec (concat (take idx pts) (drop (inc idx) pts))))))
                  ;; Save after removing point
                  (save-points-to-db))
                ;; Normal click: drag the point
                (let [start-x (.-clientX e)
                      start-y (.-clientY e)
                      [initial-x initial-y] (nth @current-points-atom idx)
                      on-move (fn [me]
                                (let [dx (- (.-clientX me) start-x)
                                      dy (- (.-clientY me) start-y)]
                                  (swap! current-points-atom
                                         assoc idx [(+ initial-x dx) (+ initial-y dy)])))
                      on-up (fn on-up-fn [_]
                              (.removeEventListener js/document "mousemove" on-move)
                              (.removeEventListener js/document "mouseup" on-up-fn)
                              ;; Save after drag completes
                              (save-points-to-db))]
                  (.addEventListener js/document "mousemove" on-move)
                  (.addEventListener js/document "mouseup" on-up)))))

          w (or img-width 800)
          h (or img-height 600)]
      [:svg {:class (str polygon-type "-editing-overlay")
             :viewBox (str "0 0 " w " " h)
             :preserveAspectRatio "none"
             :style {:position "absolute"
                     :top 0
                     :left 0
                     :width "100%"
                     :height "100%"
                     :pointer-events "none"}}

       ;; Draw the closed polygon path (with wider hit area for interaction)
       (when (> (count points) 1)
         [:<>
          ;; Visible closed polygon - with optional fill
          [:polygon {:points (str/join " " (map (fn [[x y]] (str x "," y)) points))
                     :fill (if (> fill-opacity 0) color "none")
                     :fill-opacity fill-opacity
                     :stroke color
                     :stroke-width 3
                     :stroke-dasharray "8,4"
                     :style {:pointer-events "none"}}]
          ;; Invisible wider path for easier clicking (on stroke only)
          [:polygon {:points (str/join " " (map (fn [[x y]] (str x "," y)) points))
                     :fill "transparent"
                     :stroke "transparent"
                     :stroke-width 20
                     :style {:pointer-events "stroke"
                             :cursor "move"}
                     :on-mouse-down on-path-mouse-down}]])

       ;; Draw draggable corner control points
       (for [[idx [x y]] (map-indexed vector points)]
         ^{:key (str "corner-" idx)}
         [:circle {:cx x
                   :cy y
                   :r 4
                   :fill color
                   :stroke "#fff"
                   :stroke-width 2
                   :style {:pointer-events "auto"
                           :cursor "move"}
                   :on-mouse-down (partial on-corner-mouse-down idx)
                   :on-context-menu (fn [e] (.preventDefault e))}])])))

(defn scale-points-visual-overlay
  "Visual-only SVG overlay inside the map container for scale setting.
   Shows clicked points and connecting line in image coordinate space.
   Pointer-events none — all clicks fall through to the image below."
  [img-width img-height scale-points]
  (let [w (or img-width 800)
        h (or img-height 600)
        pts scale-points]
    [:svg.scale-points-overlay
     {:viewBox (str "0 0 " w " " h)
      :preserveAspectRatio "none"
      :style {:position "absolute"
              :top 0
              :left 0
              :width "100%"
              :height "100%"
              :pointer-events "none"}}
     ;; Draw line between the two points
     (when (= 2 (count pts))
       (let [[x1 y1] (first pts)
             [x2 y2] (second pts)]
         [:line {:x1 x1 :y1 y1 :x2 x2 :y2 y2
                 :stroke "#dc3545"
                 :stroke-width 2
                 :stroke-dasharray "8,4"
                 :style {:pointer-events "none"}}]))
     ;; Draw point markers
     (for [[idx [x y]] (map-indexed vector pts)]
       ^{:key idx}
       [:circle {:cx x :cy y :r 5 :fill "#dc3545" :stroke "#fff" :stroke-width 2
                 :style {:pointer-events "none"}}])]))